/**
 * Formata o usuário de um JID WhatsApp para exibição estilo app (+55 DDD 9XXXX-XXXX).
 * Evita o padrão antigo (2+2+5+6) que quebrava números longos em blocos absurdos.
 */

function onlyDigitsBeforeAt(jid: string): string {
  const raw = (jid ?? "").split("@")[0] ?? "";
  return raw.replace(/\D/g, "");
}

/** EUA/Canadá: +1 AAA BBB-CCCC */
function formatNorthAmericaOne(digits: string): string | null {
  if (digits.length !== 11 || digits[0] !== "1") return null;
  const n = digits.slice(1);
  return `+1 ${n.slice(0, 3)} ${n.slice(3, 6)}-${n.slice(6)}`;
}

/** Brasil com código 55: +55 DD 9XXXX-XXXX (celular) ou +55 DD XXXX-XXXX (fixo) */
function formatBrazilWith55(digits: string): string | null {
  if (!digits.startsWith("55")) return null;
  const national = digits.slice(2);
  if (national.length === 11 && national[2] === "9") {
    const ddd = national.slice(0, 2);
    const rest = national.slice(2);
    return `+55 ${ddd} ${rest.slice(0, 5)}-${rest.slice(5)}`;
  }
  if (national.length === 10) {
    const ddd = national.slice(0, 2);
    const rest = national.slice(2);
    return `+55 ${ddd} ${rest.slice(0, 4)}-${rest.slice(4)}`;
  }
  if (national.length === 11) {
    const ddd = national.slice(0, 2);
    const rest = national.slice(2);
    return `+55 ${ddd} ${rest.slice(0, 5)}-${rest.slice(5)}`;
  }
  return null;
}

/** BR sem 55: 11 dígitos (celular com 9) ou 10 (fixo) */
function formatBrazilLocal(digits: string): string | null {
  if (digits.length === 11 && digits[2] === "9") {
    return `+55 ${digits.slice(0, 2)} ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `+55 ${digits.slice(0, 2)} ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return null;
}

/** Portugal: +351 XXX XXX XXX (9 dígitos nacionais após 351) */
function formatPortugal(digits: string): string | null {
  if (!digits.startsWith("351") || digits.length !== 12) return null;
  const n = digits.slice(3);
  return `+351 ${n.slice(0, 3)} ${n.slice(3, 6)} ${n.slice(6)}`;
}

/**
 * Internacional genérico: +CC e o restante em grupos de 3 (sem inventar hífen no meio errado).
 * Números muito longos (ex. ids internos) — só +digits.
 */
function formatGenericInternational(digits: string): string {
  if (digits.length > 15) {
    return `+${digits}`;
  }
  if (digits.length <= 8) {
    return `+${digits}`;
  }
  let ccLen = 2;
  if (digits.startsWith("1") && digits.length >= 11) {
    const us = formatNorthAmericaOne(digits);
    if (us) return us;
  }
  if (digits.startsWith("351")) {
    const pt = formatPortugal(digits);
    if (pt) return pt;
  }
  if (digits.startsWith("55")) {
    const br = formatBrazilWith55(digits);
    if (br) return br;
  }
  const oneDigitCc = new Set(["7", "1"]);
  if (digits.length >= 11 && oneDigitCc.has(digits[0])) {
    ccLen = 1;
  } else if (digits.length >= 12 && digits.startsWith("351")) {
    ccLen = 3;
  } else if (digits.length >= 11) {
    ccLen = 2;
  }
  const cc = digits.slice(0, ccLen);
  let rest = digits.slice(ccLen);
  const parts: string[] = [`+${cc}`];
  while (rest.length > 0) {
    const take = rest.length > 4 ? 3 : rest.length;
    parts.push(rest.slice(0, take));
    rest = rest.slice(take);
  }
  return parts.join(" ");
}

/** Só dígitos, para comparar rótulo com o mesmo número (evita duplicar linha na UI). */
export function digitsOnlyFromDisplayString(s: string): string {
  return (s ?? "").replace(/\D/g, "");
}

/**
 * Texto que é essencialmente um telefone (a API às vezes manda o número em `pushName`).
 * Não deve ser usado como "nome do contacto" nem ganhar sobre nomes reais no merge.
 */
export function looksLikePhoneOnlyLabel(s: string): boolean {
  const t = (s ?? "").trim();
  if (!t) return false;
  if (/[a-záàãâéêíóôõúç]/i.test(t)) return false;
  const d = digitsOnlyFromDisplayString(t);
  return d.length >= 10 && d.length <= 15;
}

/** JID de conversa 1:1 com número real (não é @lid nem id de grupo). */
export function jidLooksLikeWhatsAppPhone(jid: string): boolean {
  const j = (jid ?? "").toLowerCase().trim();
  return j.endsWith("@s.whatsapp.net") || j.endsWith("@c.us");
}

/** WhatsApp privacidade / LID: `...@lid` ou `...@l.id` (Evolution findChats). */
export function jidIsPrivacyLid(jid: string): boolean {
  const j = (jid ?? "").toLowerCase().trim();
  return j.endsWith("@lid") || j.endsWith("@l.id");
}

/**
 * Exibe número a partir do JID (ex.: 5512987654321@s.whatsapp.net → +55 12 98765-4321).
 * Para `...@lid` ou grupo `@g.us` retorna vazio — não é E.164.
 */
export function formatJidAsWhatsAppDisplay(jid: string): string {
  if (!jidLooksLikeWhatsAppPhone(jid)) return "";

  let digits = onlyDigitsBeforeAt(jid);
  if (!digits) return "";

  digits = digits.replace(/^0+/, "");

  const br55 = formatBrazilWith55(digits);
  if (br55) return br55;

  const brLocal = formatBrazilLocal(digits);
  if (brLocal) return brLocal;

  const na = formatNorthAmericaOne(digits);
  if (na) return na;

  const pt = formatPortugal(digits);
  if (pt) return pt;

  return formatGenericInternational(digits);
}
